// React/Next imports
import { Fragment, useState } from 'react';
import { usePopper } from 'react-popper';
import Image from 'next/image';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import clsx from 'clsx';

// Hooks
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Third-party libraries
import PropTypes from 'prop-types';

// Constants/Libs
import { POST_TYPE } from '@/lib/scheduler/postType';

import Icon from '@/components/base/Icon';
// Components
import Post from '@/components/base/Post';
import { CaptionPreview } from '@/components/composite/CaptionPreview';
import PostEvent from '@/components/composite/Scheduler/Calendar/PostEvent';

import PostEventSettings from '../calendar/PostEventSettings';

const SchedulerPost = ({
    schedulerPost,
    isLoading,
    timeFormat,
    timezone,
    setDeleteModal,
    className,
    openMediaLibrary,
    onRemovePost = () => {
        /** */
    },
    onClickEditPost = () => {
        /** */
    }
}) => {
    const { t } = useTranslation('scheduler');
    const { activeSocialProfile } = useActiveSocialProfile();

    return (
        <div
            className={clsx(
                className,
                'group flex items-center rounded-lg bg-white/40 p-2.5 text-xs text-icoGray-500 dark:bg-icoDarkMode-anthracite',
                {
                    'animate-pulse': isLoading,
                    'space-x-2.5': openMediaLibrary,
                    'space-x-6 pl-6': !openMediaLibrary
                }
            )}>
            <SchedulerPost.Hour
                date={schedulerPost?.scheduled_at}
                isLoading={isLoading}
                timeFormat={timeFormat}
                timezone={timezone}
            />
            <div
                className={clsx(
                    'relative flex flex-1 rounded-md bg-white transition-all dark:bg-icoDarkMode-deepBlack',
                    {
                        'py-2 pl-3 pr-2.5': openMediaLibrary,
                        'p-3 pl-4': !openMediaLibrary,
                        'group-hover:pl-7': !isLoading && schedulerPost.state
                    }
                )}>
                {!isLoading && schedulerPost.state && (
                    <div className="absolute left-0 top-0 inline-block h-full overflow-hidden rounded-l-md">
                        <PostEvent.Status
                            status={schedulerPost?.state}
                            postId={schedulerPost?.id}
                            stackedPosts={false}
                        />
                    </div>
                )}
                <div
                    className={clsx(
                        'relative block overflow-hidden rounded-md bg-icoGray-300 dark:bg-icoDarkMode-anthracite',
                        {
                            'mr-2 h-[2.875rem] w-[2.875rem]': openMediaLibrary,
                            'mr-5 h-20 w-20': !openMediaLibrary
                        }
                    )}>
                    {!isLoading && (
                        <>
                            {schedulerPost.data?.medias && schedulerPost.data?.medias.length ? (
                                <SchedulerPost.Img
                                    src={
                                        schedulerPost?.data?.medias?.[0]?.min_thumbnail ??
                                        schedulerPost?.data?.medias?.[0]?.image ??
                                        schedulerPost?.data?.medias?.[0]?.thumbnail
                                    }
                                    postId={schedulerPost.id}
                                />
                            ) : (
                                <span className="flex h-full w-full items-center justify-center bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                                    <Icon
                                        icon="content"
                                        className="text-xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                                    />
                                </span>
                            )}
                            <Post.PostTypeIcon type={POST_TYPE[schedulerPost.type]?.icon} />
                        </>
                    )}
                </div>
                <div className="flex flex-1 flex-col justify-between">
                    {!isLoading ? (
                        <>
                            {schedulerPost.data?.caption && schedulerPost.data?.caption !== '' ? (
                                <div
                                    className={clsx('block max-h-6 w-full pr-1.5 font-GMedium', {
                                        'mt-2': !openMediaLibrary
                                    })}>
                                    <CaptionPreview
                                        className={clsx('!break-all leading-3', {
                                            'line-clamp-2 text-xxs': openMediaLibrary,
                                            'line-clamp-3 text-xs': !openMediaLibrary
                                        })}
                                        as="span"
                                        caption={schedulerPost?.data?.caption ?? ''}
                                        network={activeSocialProfile?.social_network_type}
                                        mentions={schedulerPost?.data?.mentions ?? []}
                                    />
                                </div>
                            ) : (
                                <p className="mr-3.5 mt-2 line-clamp-2 block max-h-6 w-full !break-all font-GMedium text-xxs italic">
                                    {t('feed.no_caption')}...
                                </p>
                            )}

                            <div
                                className={clsx('flex flex-wrap items-center gap-2', {
                                    'mt-1': openMediaLibrary,
                                    'mt-2.5': !openMediaLibrary
                                })}>
                                <PostEvent.AutoPost
                                    status={schedulerPost?.state}
                                    autopost={schedulerPost?.autopost ?? false}
                                    validatedAt={schedulerPost?.validated_at ?? null}
                                />
                                <PostEvent.CrossPost
                                    hasCrossPostProfiles={
                                        (schedulerPost?.cross_post_id &&
                                            Boolean(
                                                schedulerPost?.cross_post_social_profiles?.length
                                            )) ||
                                        schedulerPost?.master_id
                                    }
                                />
                            </div>
                            <SchedulerPost.Settings
                                post={schedulerPost}
                                openMediaLibrary={openMediaLibrary}
                                userTimezone={timezone}
                                setDeleteModal={setDeleteModal}
                                onRemovePost={onRemovePost}
                                onClickEditPost={onClickEditPost}
                            />
                        </>
                    ) : (
                        <>
                            <span className="mt-2 block w-full rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-anthracite"></span>
                            <span className="mt-2 block w-full rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-anthracite"></span>
                            <span className="mt-2 block w-2/3 rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-anthracite"></span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

SchedulerPost.propTypes = {
    schedulerPost: PropTypes.object,
    isLoading: PropTypes.bool,
    timeFormat: PropTypes.string,
    timezone: PropTypes.string,
    className: PropTypes.string
};

const Hour = ({ date, isLoading, timezone, timeFormat }) => {
    const { formatDateTime } = useFormatDateTime(timezone);
    const hourPost = !isLoading ? (
        formatDateTime(date, `${timeFormat}_noSeconds`)
    ) : (
        <span className="inline-block w-8 bg-icoGray-400/50 py-2 dark:bg-icoDarkMode-deepBlack"></span>
    );

    return (
        <span className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-white uppercase dark:border-icoDarkMode-beton">
            {hourPost}
        </span>
    );
};

SchedulerPost.Hour = Hour;

Hour.propTypes = {
    date: PropTypes.string,
    isLoading: PropTypes.bool,
    timeFormat: PropTypes.string,
    timezone: PropTypes.string
};

const Settings = ({
    post,
    userTimezone,
    setDeleteModal,
    onRemovePost = () => {
        /** */
    },
    onClickEditPost = () => {
        /** */
    },
    openMediaLibrary = false
}) => {
    const [referenceElement, setReferenceElement] = useState();
    const [popperElement, setPopperElement] = useState();
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-end',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [3, 0]
                }
            }
        ]
    });

    return (
        <Popover as={Fragment}>
            {({ open }) => (
                <>
                    <PopoverButton
                        data-testid="post-settings"
                        ref={setReferenceElement}
                        className={clsx(
                            'absolute z-1 h-4 w-m align-middle text-icoGray-500 focus:ring-0 focus:ring-offset-0 dark:text-icoDarkMode-wolf',
                            {
                                'right-0 top-0 text-xxs': openMediaLibrary,
                                'right-2 top-1 text-sm': !openMediaLibrary
                            }
                        )}>
                        <Icon icon="dots" />
                    </PopoverButton>
                    <div
                        ref={setPopperElement}
                        style={styles.popper}
                        {...attributes.popper}
                        className="z-20">
                        <Transition
                            show={open}
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1">
                            <PopoverPanel>
                                <div className="rounded-md border border-icoGray-300 bg-white shadow-icoM dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite">
                                    <PostEventSettings
                                        post={post}
                                        timezone={userTimezone}
                                        toggleDeleteModal={() => {
                                            if (
                                                post?.id?.toString().split('-')[0] === 'draggable'
                                            ) {
                                                onRemovePost(post.id);
                                            } else {
                                                setDeleteModal((prevState) => ({
                                                    ...prevState,
                                                    post: post,
                                                    isOpen: true
                                                }));
                                            }
                                        }}
                                        onClickEditPost={onClickEditPost}
                                    />
                                </div>
                            </PopoverPanel>
                        </Transition>
                    </div>
                </>
            )}
        </Popover>
    );
};

SchedulerPost.Settings = Settings;

Settings.propTypes = {
    post: PropTypes.object,
    userTimezone: PropTypes.string,
    setDeleteModal: PropTypes.func
};

/**
 * Image with error handler
 */

const Img = ({ postId, src }) => {
    const [errorImg, setErrorImage] = useState(false);

    if (errorImg || !src) {
        return (
            <span className="flex h-full w-full items-center justify-center bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                {/* Change Icon to real fallback */}
                <Icon
                    icon="content"
                    className="text-2xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                />
            </span>
        );
    }

    return (
        <span className="relative block h-full w-full">
            <Image
                src={src}
                alt={postId}
                width={200}
                height={250}
                className="rendering-crisp-edges h-full w-full object-cover"
                onError={() => setErrorImage(true)}
            />
        </span>
    );
};

SchedulerPost.Img = Img;

Img.propTypes = {
    postId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    media: PropTypes.string
};

export default SchedulerPost;
